import React, { useState, useEffect } from 'react';
import { getRoster, createShift, getPersonnel } from '../services/api';
import { Calendar, Plus, Clock } from 'lucide-react';
import { format, addDays, startOfWeek } from 'date-fns';

const SHIFT_TYPES = [
  { value: 'morning', label: 'Morning (06:00–14:00)', start: '06:00', end: '14:00', color: 'var(--accent-amber)' },
  { value: 'afternoon', label: 'Afternoon (14:00–22:00)', start: '14:00', end: '22:00', color: 'var(--accent-blue)' },
  { value: 'night', label: 'Night (22:00–06:00)', start: '22:00', end: '06:00', color: 'var(--accent-purple)' },
];

export default function Roster() {
  const [roster, setRoster] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ personnel_id: '', shift_date: '', shift_type: 'morning' });

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  useEffect(() => {
    loadData();
  }, [weekStart]);

  const loadData = async () => {
    try {
      const [rosterRes, personRes] = await Promise.all([
        getRoster({ date: undefined }),
        getPersonnel()
      ]);
      setRoster(rosterRes.data.roster || []);
      setPersonnel(personRes.data.personnel || []);
    } catch (err) { console.error(err); }
  };

  const handleAddShift = async () => {
    const shiftType = SHIFT_TYPES.find(s => s.value === form.shift_type);
    try {
      await createShift({
        personnel_id: form.personnel_id,
        shift_date: form.shift_date,
        shift_start: shiftType?.start || '08:00',
        shift_end: shiftType?.end || '16:00',
        shift_type: form.shift_type
      });
      await loadData();
      setShowAdd(false);
    } catch (err) { alert(err.response?.data?.error || 'Failed to schedule shift'); }
  };

  const getShiftsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return roster.filter(r => r.shift_date?.substring(0, 10) === dateStr);
  };

  return (
    <div style={{ padding: 24, overflow: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 4 }}>Shift Roster</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Week of {format(weekStart, 'MMM d')} — {format(addDays(weekStart, 6), 'MMM d, yyyy')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" onClick={() => setWeekStart(addDays(weekStart, -7))}>← Prev</button>
          <button className="btn" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>Today</button>
          <button className="btn" onClick={() => setWeekStart(addDays(weekStart, 7))}>Next →</button>
          <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}><Plus size={14} /> Add Shift</button>
        </div>
      </div>

      {showAdd && (
        <div className="panel" style={{ marginBottom: 20, padding: 20 }}>
          <div className="panel-title" style={{ marginBottom: 16 }}>SCHEDULE SHIFT</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <div>
              <label className="form-label">Personnel</label>
              <select className="form-input" value={form.personnel_id} onChange={e => setForm({ ...form, personnel_id: e.target.value })}>
                <option value="">-- Select --</option>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.full_name} ({p.team_name || 'Unassigned'})</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={form.shift_date} onChange={e => setForm({ ...form, shift_date: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Shift</label>
              <select className="form-input" value={form.shift_type} onChange={e => setForm({ ...form, shift_type: e.target.value })}>
                {SHIFT_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleAddShift}>Schedule</button>
            <button className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Week Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
        {days.map(day => {
          const shifts = getShiftsForDay(day);
          const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
          return (
            <div key={day.toISOString()} style={{
              background: 'var(--bg-panel)', border: `1px solid ${isToday ? 'var(--accent-blue)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-md)', minHeight: 180, overflow: 'hidden'
            }}>
              <div style={{
                padding: '10px 12px', borderBottom: '1px solid var(--border)',
                background: isToday ? 'rgba(59, 130, 246, 0.08)' : 'var(--bg-card)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                    {format(day, 'EEE')}
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700 }}>
                    {format(day, 'd')}
                  </div>
                </div>
                {shifts.length > 0 && (
                  <span style={{ fontSize: 10, background: 'var(--accent-blue)', color: '#fff', padding: '2px 6px', borderRadius: 8, fontWeight: 700 }}>
                    {shifts.length}
                  </span>
                )}
              </div>
              <div style={{ padding: 8 }}>
                {shifts.map(s => {
                  const type = SHIFT_TYPES.find(t => t.value === s.shift_type);
                  return (
                    <div key={s.id} style={{
                      padding: '6px 8px', marginBottom: 4, borderRadius: 4,
                      background: `${type?.color || 'var(--accent-blue)'}15`,
                      borderLeft: `3px solid ${type?.color || 'var(--accent-blue)'}`,
                      fontSize: 11
                    }}>
                      <div style={{ fontWeight: 600 }}>{s.full_name}</div>
                      <div style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                        <Clock size={9} /> {s.shift_start?.substring(0, 5)} — {s.shift_end?.substring(0, 5)}
                      </div>
                    </div>
                  );
                })}
                {shifts.length === 0 && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', padding: 10 }}>No shifts</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
